import { RequestTypeOf } from "../generated/routes";
import {
  Account,
  AllDataAccount,
  AllDataDevice,
  Breeding,
  BreedingList,
  Building,
  Company,
  CreateBreeding,
  DeviceWithoutLegacyPassword,
} from "../generated/types";
import { I18nKey } from "../i18n/i18n";
import { isAdminAccount } from "../utils/roles";
import { apiFetchJson, apiFetchRoute } from "./apiFetchRoute";
import { fetchPartnerships } from "./partnership";

export type AllDataBreeding = AccessibleBreeding & {
  company: Company;
  buildings: Building[];
  devices: DeviceWithoutLegacyPassword[];
  accounts: Account[];
};

type AccessibleBreeding = {
  breeding: Breeding;
  /** Le client n'a partagé qu'un bâtiment ou un produit : l'élevage arrive sans adresse. */
  isPartial: boolean;
  canEdit: boolean;
};

// Champs que GET /breedings ajoute à chaque élevage (absents de /admin/breedings). Ils sont
// retirés de `breeding`, que PUT /breedings refuserait à cause d'un champ inconnu.
type BreedingAccessFields = { access?: "full" | "partial"; can_edit?: boolean };

const normalizeBreedings = (
  data: BreedingList | null | undefined
): AccessibleBreeding[] =>
  (Array.isArray(data) ? data : []).map(
    ({ access, can_edit, ...breeding }: Breeding & BreedingAccessFields) => ({
      breeding,
      isPartial: access === "partial",
      canEdit: can_edit ?? isAdminAccount(),
    })
  );

const indexDevicesByBreedingId = (devices: AllDataDevice[]) => {
  const devicesByBreedingId = new Map<number, DeviceWithoutLegacyPassword[]>();

  for (const entry of devices) {
    if (!entry?.breeding?.id || !entry.device) {
      continue;
    }

    const list = devicesByBreedingId.get(entry.breeding.id) ?? [];
    if (!list.some((device) => device.id === entry.device.id)) {
      list.push(entry.device);
    }
    devicesByBreedingId.set(entry.breeding.id, list);
  }

  return devicesByBreedingId;
};

const indexDevicesByBreedingIdFromBuildings = (
  buildings: Building[],
  devices: DeviceWithoutLegacyPassword[]
) => {
  const devicesById = new Map(devices.map((device) => [device.id, device]));
  const devicesByBreedingId = new Map<number, DeviceWithoutLegacyPassword[]>();

  for (const building of buildings) {
    const list = devicesByBreedingId.get(building.id_breeding) ?? [];

    for (const deviceId of building.device ?? []) {
      const device = devicesById.get(deviceId);
      if (device && !list.some((entry) => entry.id === device.id)) {
        list.push(device);
      }
    }

    devicesByBreedingId.set(building.id_breeding, list);
  }

  return devicesByBreedingId;
};

const indexBuildingsByBreedingId = (buildings: Building[]) => {
  const buildingsByBreedingId = new Map<number, Building[]>();

  for (const building of buildings) {
    const list = buildingsByBreedingId.get(building.id_breeding) ?? [];
    list.push(building);
    buildingsByBreedingId.set(building.id_breeding, list);
  }

  return buildingsByBreedingId;
};

/** Produits qu'aucun bâtiment ne contient (une Vigebox, une Serenity pas encore installée). */
export type UnassignedDevices = {
  companyId: number;
  companyName: string;
  devices: DeviceWithoutLegacyPassword[];
};

export type BreedingsData = {
  breedings: AllDataBreeding[];
  unassignedDevices: UnassignedDevices[];
};

/**
 * Regroupe par entreprise les produits sans bâtiment. Seules les entreprises de
 * `companyNames` sont retenues : un installateur liste ceux de ses clients, pas son stock.
 */
export const groupUnassignedDevices = (
  buildings: Building[],
  devices: DeviceWithoutLegacyPassword[],
  companyNames: Map<number, string>
): UnassignedDevices[] => {
  const assigned = new Set(
    buildings.flatMap((building) => building.device ?? [])
  );
  return Array.from(companyNames, ([companyId, companyName]) => ({
    companyId,
    companyName,
    devices: devices.filter(
      (device) => device.id_company === companyId && !assigned.has(device.id)
    ),
  })).filter((group) => group.devices.length > 0);
};

const fallbackCompany = (breeding: Breeding): Company => ({
  id: breeding.id_company,
  creation_date: "",
  company_name: "-",
  address: "",
  zip_code: "",
  city: "",
  country: breeding.country,
  phone_mobile: "",
  phone_fixe: "",
  email: "",
});

const toAllDataBreedings = ({
  breedings,
  companyById,
  buildingsByBreedingId,
  devicesByBreedingId,
  accountsByCompanyId,
}: {
  breedings: AccessibleBreeding[];
  companyById: Map<number, Company>;
  buildingsByBreedingId: Map<number, Building[]>;
  devicesByBreedingId: Map<number, DeviceWithoutLegacyPassword[]>;
  accountsByCompanyId: Map<number, Account[]>;
}): AllDataBreeding[] =>
  breedings.map(({ breeding, isPartial, canEdit }) => ({
    breeding,
    isPartial,
    canEdit,
    company: companyById.get(breeding.id_company) ?? fallbackCompany(breeding),
    buildings: buildingsByBreedingId.get(breeding.id) ?? [],
    devices: devicesByBreedingId.get(breeding.id) ?? [],
    accounts: accountsByCompanyId.get(breeding.id_company) ?? [],
  }));

/** Les quatre listes admin brutes, de quoi relier élevages, bâtiments, produits et comptes. */
export type AdminRawData = {
  breedings: BreedingList;
  devices: AllDataDevice[];
  buildings: Building[];
  users: AllDataAccount[];
};

export const fetchAdminRawData = async (): Promise<AdminRawData> => {
  const [breedings, devices, buildings, users] = await Promise.all([
    apiFetchJson("getAdminBreedings", undefined),
    apiFetchJson("getAdminDevicesAlldata", undefined),
    apiFetchJson("getAdminBuildings", undefined),
    apiFetchJson("getAdminAccountsAlldata", undefined),
  ]);
  return {
    breedings: breedings ?? [],
    devices: devices ?? [],
    buildings: buildings ?? [],
    users: users ?? [],
  };
};

export const assembleAdminBreedings = ({
  breedings: breedingsData,
  devices,
  buildings,
  users,
}: AdminRawData): AllDataBreeding[] => {
  const breedings = normalizeBreedings(breedingsData);

  const companyById = new Map<number, Company>();
  const accountsByCompanyId = new Map<number, Account[]>();
  const buildingsByBreedingId = indexBuildingsByBreedingId(buildings);
  const devicesByBreedingId = indexDevicesByBreedingId(devices);

  for (const user of users) {
    companyById.set(user.company.id, user.company);
    const accounts = accountsByCompanyId.get(user.company.id) ?? [];
    accounts.push(user.account);
    accountsByCompanyId.set(user.company.id, accounts);
  }

  for (const entry of devices) {
    companyById.set(entry.company.id, entry.company);
  }

  return toAllDataBreedings({
    breedings,
    companyById,
    buildingsByBreedingId,
    devicesByBreedingId,
    accountsByCompanyId,
  });
};

/**
 * Élevages visibles pour un éleveur (ou tout non-admin).
 * Routes scopées : GET /breedings, GET /buildings, GET /devices.
 */
export const fetchFarmerAllDataBreedings = async (): Promise<BreedingsData> => {
  const [breedingsData, devicesData, buildingsData, profileData] =
    await Promise.all([
      apiFetchJson("getBreedings", undefined),
      apiFetchJson("getDevices", undefined),
      apiFetchJson("getBuildings", undefined),
      apiFetchJson("getAccountProfile", undefined),
    ]);

  const breedings = normalizeBreedings(breedingsData);
  const devices = devicesData ?? [];
  const buildings = buildingsData ?? [];
  const profile = profileData as
    | { account?: { id_company: number }; company?: Company }
    | undefined;

  const companyById = new Map<number, Company>();
  if (profile?.company) {
    companyById.set(profile.company.id, profile.company);
  }

  const buildingsByBreedingId = indexBuildingsByBreedingId(buildings);
  const devicesByBreedingId = indexDevicesByBreedingIdFromBuildings(
    buildings,
    devices
  );

  return {
    breedings: toAllDataBreedings({
      breedings,
      companyById,
      buildingsByBreedingId,
      devicesByBreedingId,
      accountsByCompanyId: new Map<number, Account[]>(),
    }),
    unassignedDevices: groupUnassignedDevices(
      buildings,
      devices,
      new Map(
        profile?.company
          ? [[profile.company.id, profile.company.company_name]]
          : []
      )
    ),
  };
};

const BREEDER_TYPES = new Set(["breeder", "breeder_assistant"]);

/**
 * Élevages de mes clients, vus par un installateur.
 *
 * Les routes scopées renvoient à l'installateur ce que ses clients lui partagent ; on ne
 * garde en plus que les élevages d'un partenariat accepté, pour ne jamais lister un
 * élevage de sa propre entreprise comme celui d'un client.
 */
export const fetchProviderAllDataBreedings =
  async (): Promise<BreedingsData> => {
    const [breedingsData, devicesData, buildingsData, accountsData, partners] =
      await Promise.all([
        apiFetchJson("getBreedings", undefined),
        apiFetchJson("getDevices", undefined),
        apiFetchJson("getBuildings", undefined),
        apiFetchJson("getAccounts", undefined),
        fetchPartnerships({ role: "provider", status: "accepted" }),
      ]);
    if (partners.error) throw new Error(partners.error);

    const clientNames = new Map(
      partners.partnerships.map((p) => [p.company.id, p.company.company_name])
    );
    const breedings = normalizeBreedings(breedingsData).filter(({ breeding }) =>
      clientNames.has(breeding.id_company)
    );
    const companyIds = Array.from(
      new Set(breedings.map(({ breeding }) => breeding.id_company))
    );

    // Fiche complète de chaque client (adresse, téléphone) ; à défaut, le nom connu par le
    // partenariat suffit à la liste.
    const companies = await Promise.all(
      companyIds.map((id) =>
        apiFetchJson("getCompanyId", undefined, { params: { id } })
          .then((company) => company as Company | undefined)
          .catch(() => undefined)
      )
    );
    const companyById = new Map(
      companyIds.map((id, index): [number, Company] => [
        id,
        companies[index] ?? {
          ...fallbackCompany(
            breedings.find(({ breeding }) => breeding.id_company === id)!
              .breeding
          ),
          company_name: clientNames.get(id) ?? "-",
        },
      ])
    );

    const breederAccounts = ((accountsData ?? []) as Account[]).filter(
      (account) => BREEDER_TYPES.has(account.type)
    );
    const accountsByCompanyId = new Map(
      companyIds.map((id): [number, Account[]] => [
        id,
        breederAccounts.filter((account) => account.id_company === id),
      ])
    );

    const buildings = (buildingsData ?? []) as Building[];
    const devices = devicesData ?? [];
    return {
      breedings: toAllDataBreedings({
        breedings,
        companyById,
        buildingsByBreedingId: indexBuildingsByBreedingId(buildings),
        devicesByBreedingId: indexDevicesByBreedingIdFromBuildings(
          buildings,
          devices
        ),
        accountsByCompanyId,
      }),
      unassignedDevices: groupUnassignedDevices(
        buildings,
        devices,
        clientNames
      ),
    };
  };

export const fetchAllDataBreedings = async (): Promise<{
  breedings: AllDataBreeding[];
  error: string | null;
}> => {
  try {
    const allDataBreedings = isAdminAccount()
      ? assembleAdminBreedings(await fetchAdminRawData())
      : (await fetchFarmerAllDataBreedings()).breedings;

    return { breedings: allDataBreedings, error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { breedings: [], error: "Impossible de charger les élevages" };
  }
};

export const fetchBreedings = async (): Promise<{
  breedings: Breeding[];
  error: string | null;
}> => {
  try {
    const data = isAdminAccount()
      ? await apiFetchJson("getAdminBreedings", undefined)
      : await apiFetchJson("getBreedings", undefined);

    return {
      breedings: normalizeBreedings(data).map(({ breeding }) => breeding),
      error: null,
    };
  } catch (err) {
    console.error("Erreur:", err);
    return { breedings: [], error: "Impossible de charger les élevages" };
  }
};

export const createBreeding = async (
  formData: CreateBreeding
): Promise<{
  success: boolean;
  breedingId?: number;
  error?: string | null;
}> => {
  try {
    type BreedingRequest = RequestTypeOf<"postBreedings">;

    const body: BreedingRequest = formData;

    const breedingResponse = await apiFetchRoute("postBreedings", body, {
      headers: {
        "Content-type": "application/json",
      },
    });

    if (!breedingResponse.ok) {
      return {
        success: false,
        error: "Une erreur est survenue lors de la création de l'élevage",
      };
    }

    const breedingData = await breedingResponse.json();
    return {
      success: true,
      breedingId: Number(breedingData.id_breeding || breedingData.id),
    };
  } catch (error) {
    console.error("Erreur lors de la création de l'élevage:", error);
    return {
      success: false,
      error: "Une erreur est survenue lors de la création de l'élevage",
    };
  }
};

export const updateBreeding = async (
  formData: Breeding
): Promise<{
  success: boolean;
  error?: string | null;
}> => {
  try {
    type BreedingRequest = RequestTypeOf<"putBreedings">;

    const body: BreedingRequest = formData;

    const breedingResponse = await apiFetchRoute("putBreedings", body, {
      headers: {
        "Content-type": "application/json",
      },
    });

    if (!breedingResponse.ok) {
      return {
        success: false,
        error: "Une erreur est survenue lors de la modification de l'élevage",
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error("Erreur lors de la modification de l'élevage:", error);
    return {
      success: false,
      error: "Une erreur est survenue lors de la modification de l'élevage",
    };
  }
};

/**
 * Supprime un élevage. L'API refuse (409) tant qu'il a des bâtiments.
 * Rend la clé du message d'erreur, vide en cas de succès.
 */
export const deleteBreeding = async (id: number): Promise<I18nKey | ""> => {
  try {
    const response = await apiFetchRoute("deleteBreedingsId", undefined, {
      params: { id },
    });
    if (response.ok) return "";
    // i18n.t("breeding-delete-failed")
    return response.status === 409
      ? "breeding-delete-not-empty"
      : "breeding-delete-failed";
  } catch (error) {
    console.error("Erreur lors de la suppression de l'élevage:", error);
    return "breeding-delete-failed";
  }
};
